import React from "react";
import { Panel, useReactFlow } from "@xyflow/react";
import { ZoomIn, ZoomOut, RotateCw, Maximize2, Minimize2 } from "react-feather";
import { Tooltip } from "../../atoms/Tooltip";
import { Container, ControlButton, Divider } from "./KgCanvasControls.styles";

// Design spec: stroke-width 1px, stroke #5C5468 (text.mediumEmphasis via currentColor)
const ICON_SIZE = 32;
const ICON_STROKE = 1;

const FitViewIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 4V2a1 1 0 0 1 1-1h2"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M1 12v2a1 1 0 0 0 1 1h2"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15 4V2a1 1 0 0 0-1-1h-2"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M15 12v2a1 1 0 0 1-1 1h-2"
      stroke="currentColor"
      strokeWidth="1"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export type KgCanvasControlsVariant = "default" | "zoomOnly";
export type KgCanvasControlsOffset = Partial<
  Record<"top" | "right" | "bottom" | "left", number | string>
>;

export interface KgCanvasControlsProps {
  zoomStep?: number;
  onExpand?: () => void;
  isExpanded?: boolean;
  /** `zoomOnly` — preview builder: +/- only (Figma). */
  variant?: KgCanvasControlsVariant;
  /** Optional explicit panel offsets, e.g. { left: 16, bottom: 16 }. */
  panelOffset?: KgCanvasControlsOffset;
}

const ZOOM_ONLY_ICON_SIZE = 16;
const DEFAULT_PANEL_OFFSET: Required<KgCanvasControlsOffset> = {
  top: "auto",
  right: 10,
  bottom: 10,
  left: "auto",
};

export const KgCanvasControls: React.FC<KgCanvasControlsProps> = ({
  zoomStep = 1.2,
  onExpand,
  isExpanded = false,
  variant = "default",
  panelOffset,
}) => {
  const { zoomIn, zoomOut, fitView } = useReactFlow();
  const isZoomOnly = variant === "zoomOnly";
  const iconSize = isZoomOnly ? ZOOM_ONLY_ICON_SIZE : ICON_SIZE;
  const panelStyle = { ...DEFAULT_PANEL_OFFSET, ...panelOffset };
  const tooltipPosition =
    panelStyle.left !== "auto" && panelStyle.right === "auto" ? "right" : "left";

  if (isZoomOnly) {
    return (
      <Panel position="bottom-right" style={panelStyle}>
        <Container $compact data-testid="kg-canvas-controls-zoom-only">
          <Tooltip label="Zoom in" position={tooltipPosition}>
            <ControlButton
              $compact
              onClick={() => zoomIn({ duration: 200 })}
              aria-label="Zoom in"
            >
              <ZoomIn size={iconSize} strokeWidth={ICON_STROKE} />
            </ControlButton>
          </Tooltip>
          <Tooltip label="Zoom out" position={tooltipPosition}>
            <ControlButton
              $compact
              onClick={() => zoomOut({ duration: 200 })}
              aria-label="Zoom out"
            >
              <ZoomOut size={iconSize} strokeWidth={ICON_STROKE} />
            </ControlButton>
          </Tooltip>
        </Container>
      </Panel>
    );
  }

  return (
    <Panel position="bottom-right" style={panelStyle}>
      <Container>
        <Tooltip label="Zoom in" position={tooltipPosition}>
          <ControlButton
            onClick={() => zoomIn({ duration: 200 })}
            aria-label="Zoom in"
          >
            <ZoomIn size={iconSize} strokeWidth={ICON_STROKE} />
          </ControlButton>
        </Tooltip>

        <Tooltip label="Zoom out" position={tooltipPosition}>
          <ControlButton
            onClick={() => zoomOut({ duration: 200 })}
            aria-label="Zoom out"
          >
            <ZoomOut size={iconSize} strokeWidth={ICON_STROKE} />
          </ControlButton>
        </Tooltip>

        <Divider />

        <Tooltip label="Fit to screen" position={tooltipPosition}>
          <ControlButton
            onClick={() => fitView({ duration: 300, padding: 0.1 })}
            aria-label="Fit to screen"
          >
            <FitViewIcon />
          </ControlButton>
        </Tooltip>

        <Divider />

        {onExpand && (
          <Tooltip
            label={isExpanded ? "Exit fullscreen" : "Full screen"}
            position={tooltipPosition}
          >
            <ControlButton
              onClick={onExpand}
              aria-label={isExpanded ? "Exit fullscreen" : "Full screen"}
            >
              {isExpanded ? (
                <Minimize2 size={iconSize} strokeWidth={ICON_STROKE} />
              ) : (
                <Maximize2 size={iconSize} strokeWidth={ICON_STROKE} />
              )}
            </ControlButton>
          </Tooltip>
        )}

        <Divider />

        <Tooltip label="Rearrange" position={tooltipPosition}>
          <ControlButton
            onClick={() => fitView({ duration: 400, padding: 0.15 })}
            aria-label="Rearrange"
          >
            <RotateCw size={iconSize} strokeWidth={ICON_STROKE} />
          </ControlButton>
        </Tooltip>
      </Container>
    </Panel>
  );
};

export default KgCanvasControls;
